import { useEffect, useRef, useState } from 'react'
import QRCode from 'qrcode'
import { ChevronRight, ExternalLink, Mail, MessageSquare, QrCode, Smartphone } from 'lucide-react'
import { apiClient } from '@/lib/api'
import { cn } from '@/lib/utils'
import { billingErrorMessage } from '@/lib/billing'
import { cardFeePreviewCents, type CardFeeSettings } from '@/lib/cardFees'

/**
 * Card or bank payments, all on Stripe's hosted page so card numbers never
 * touch the CRM:
 *   qr     — the customer scans and pays on their own phone (Apple Pay,
 *            Google Pay, card or bank on Stripe's page)
 *   send   — a single-use link by text and/or the invoice PDF by email
 * The screen watches Stripe and moves on by itself when the money lands.
 */

export type Contact = { id: string; firstName: string; lastName: string; phone?: string | null; email?: string | null }
export type CardInvoice = { id: string; invoiceNumber: string }
export type Channel = 'sms' | 'email'
export type CardMode = 'qr' | 'send' | null
/** POST /payments/invoices/:id/link — the server's fee is what the customer pays. */
export type PayLink = { id: string; url: string; amountCents?: number; feeCents?: number; disclosure?: string | null }
export type CardDone =
  | { kind: 'card'; amountCents: number }
  | { kind: 'bank-pending'; amountCents: number }
  | { kind: 'link'; amountCents: number; url: string; sentVia: Channel[]; sendErrors: string[] }

const POLL_MS = 2500
/** Money with cents, always (VOICE: "$1,296.00", never "$1,296"; UX-095). */
export const dollars = (cents: number) =>
  (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
export const phoneOf = (p?: string | null) => {
  const d = (p ?? '').replace(/\D/g, '').slice(-10)
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p ?? ''
}
/** Plain-language failure text (coded billing copy or F2's describeError); never a raw server string. */
export const apiError = (e: unknown, fallback = 'That didn’t go through. Try again.') => billingErrorMessage(e, fallback)
const validPhone = (p: string) => p.replace(/\D/g, '').length === 10 || /^\+1\d{10}$/.test(p.replace(/[^\d+]/g, ''))
const validEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim())

type Props = {
  invoice: CardInvoice
  customer: Contact
  amountCents: number
  /** Workspace card-fee settings (Settings → Money); null = not passed on. */
  cardFees?: CardFeeSettings | null
  business: string
  mode: CardMode
  setMode: (m: CardMode) => void
  onDone: (d: CardDone) => void
  onContact: (c: Contact) => void
}

export function CardPayment({ invoice, customer, amountCents, cardFees = null, business, mode, setMode, onDone, onContact }: Props) {
  const [busy, setBusy] = useState(false)
  const createLink = async () => (await apiClient.post(`/api/payments/invoices/${invoice.id}/link`, { amountCents })).data.data as PayLink
  // Card fee pass-through (DECISIONS §22): disclosed before paying; a link
  // that carries the fee takes cards only, so bank isn't offered then.
  const feePreview = cardFeePreviewCents(amountCents, cardFees)
  const methods = feePreview > 0 ? 'Apple Pay, Google Pay or card' : 'Apple Pay, Google Pay, card or bank'

  // Each sub-screen gets its own key so it animates in.
  if (mode === 'qr') return <div className="pay-step" key="qr"><QrPay invoice={invoice} customer={customer} amountCents={amountCents} createLink={createLink} onDone={onDone} /></div>
  if (mode === 'send') return <div className="pay-step" key="send"><SendLink invoice={invoice} customer={customer} amountCents={amountCents} business={business} createLink={createLink} onDone={onDone} onContact={onContact} busy={busy} setBusy={setBusy} /></div>

  return (
    <div className="pay-step" key="choose">
      <h3 className="pay-sec">{customer.firstName} is with you</h3>
      <ul className="ds-set-card pay-list">
        <li>
          <button type="button" className="pay-row" onClick={() => setMode('qr')}>
            <span className="ds-ico pay-ico is-white" aria-hidden><QrCode /></span>
            <span className="pay-row-l"><b>Scan to pay on their phone</b><span>{methods}</span></span>
            <ChevronRight className="pay-chev" aria-hidden />
          </button>
        </li>
      </ul>
      <h3 className="pay-sec">Not with you</h3>
      <ul className="ds-set-card pay-list">
        <li>
          <button type="button" className="pay-row" onClick={() => setMode('send')}>
            <span className="ds-ico pay-ico" aria-hidden><Smartphone /></span>
            <span className="pay-row-l"><b>Text or email a payment link</b><span>The link works once, for {dollars(amountCents)}{feePreview > 0 ? ` + ${dollars(feePreview)} card fee` : ''}</span></span>
            <ChevronRight className="pay-chev" aria-hidden />
          </button>
        </li>
      </ul>
      {feePreview > 0 && cardFees?.disclosure && <p className="pay-note pay-center" data-testid="card-fee-disclosure">{cardFees.disclosure}</p>}
      <p className="pay-note pay-center">Card details are entered on Stripe’s secure page, never in this app.</p>
    </div>
  )
}

/** "$1,000 + $29 card processing fee" — what the customer will be charged. */
function FeeLine({ link }: { link: PayLink | null }) {
  if (!link?.feeCents) return null
  return (
    <div className="pay-fee" data-testid="card-fee-line">
      <p className="pay-note pay-center"><b>{dollars(link.amountCents ?? 0)}</b> + <b>{dollars(link.feeCents)}</b> card processing fee = <b>{dollars((link.amountCents ?? 0) + link.feeCents)}</b> by card</p>
      {link.disclosure && <p className="pay-note pay-center">{link.disclosure}</p>}
    </div>
  )
}

// ── Scan to pay ────────────────────────────────────────────────────────────
function QrPay({ invoice, customer, amountCents, createLink, onDone }: { invoice: CardInvoice; customer: Contact; amountCents: number; createLink: () => Promise<PayLink>; onDone: (d: CardDone) => void }) {
  const [link, setLink] = useState<PayLink | null>(null)
  const [img, setImg] = useState('')
  const [error, setError] = useState('')

  const created = useRef(false)
  useEffect(() => {
    if (created.current) return
    created.current = true
    createLink()
      .then(async l => { setLink(l); setImg(await QRCode.toDataURL(l.url, { margin: 1, width: 560, color: { dark: '#000000', light: '#FFFFFF' } })) })
      .catch(e => setError(apiError(e, 'Couldn’t create the payment page.')))
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!link) return
    const t = window.setInterval(async () => {
      try {
        const res = await apiClient.get(`/api/payments/invoices/${invoice.id}/link-status`, { params: { linkId: link.id } })
        const p = res.data.data.payment as { status: string } | null
        if (p?.status === 'SUCCEEDED') onDone({ kind: 'card', amountCents })
        else if (p?.status === 'PENDING') onDone({ kind: 'bank-pending', amountCents })
      } catch { /* keep waiting */ }
    }, POLL_MS)
    return () => window.clearInterval(t)
  }, [link]) // eslint-disable-line react-hooks/exhaustive-deps

  if (error) return <p role="alert" className="pay-error">{error}</p>
  return (
    <div className="pay-wait">
      <div className="pay-qr">{img ? <img src={img} alt={`QR code to pay ${dollars(amountCents)}`} /> : <span className="shimmer" />}</div>
      <h2 className="pay-q pay-center">Have {customer.firstName} scan this</h2>
      <p className="pay-note pay-center">With their phone camera. They pay on Stripe’s page with Apple Pay, Google Pay, a card{link?.feeCents ? '' : ' or their bank'}.</p>
      <FeeLine link={link} />
      <p className="pay-status" role="status">{link ? 'Waiting for payment…' : 'Creating the payment page…'}</p>
      {link && (
        <a className="ds-btn ds-btn-lg pay-wide" href={link.url} target="_blank" rel="noopener noreferrer">
          <ExternalLink aria-hidden /> Open it on this device instead
        </a>
      )}
      {link && <p className="pay-note pay-center">Only hand your device over if they’ll type their own card. Apple Pay here would charge your wallet.</p>}
    </div>
  )
}

// ── Send a link ────────────────────────────────────────────────────────────
function SendLink({ invoice, customer, amountCents, business, createLink, onDone, onContact, busy, setBusy }: { invoice: CardInvoice; customer: Contact; amountCents: number; business: string; createLink: () => Promise<PayLink>; onDone: (d: CardDone) => void; onContact: (c: Contact) => void; busy: boolean; setBusy: (b: boolean) => void }) {
  const [text, setText] = useState(!!customer.phone)
  const [email, setEmail] = useState(!!customer.email || !customer.phone)
  const [phone, setPhone] = useState(customer.phone ?? '')
  const [addr, setAddr] = useState(customer.email ?? '')
  const [save, setSave] = useState(true)
  const [error, setError] = useState('')

  const needsPhone = text && !customer.phone
  const needsEmail = email && !customer.email
  const phoneOk = !text || validPhone(phone)
  const emailOk = !email || validEmail(addr)
  // The text carries the fee so the customer knows before they tap (§22).
  const feeNote = (fee: number) => (fee > 0 ? ` Paying by card adds a ${dollars(fee)} card processing fee; it isn’t part of your invoice.` : '')
  const message = (url: string, fee = 0) => `Hi ${customer.firstName}, here’s your secure payment link from ${business || 'us'} for invoice ${invoice.invoiceNumber} (${dollars(amountCents)}): ${url}${feeNote(fee)}`
  const label = text && email ? `Text & email ${dollars(amountCents)} link` : text ? `Text ${dollars(amountCents)} link` : email ? `Email invoice + ${dollars(amountCents)} link` : 'Choose text or email'

  const send = async () => {
    setBusy(true); setError('')
    let link: PayLink
    try { link = await createLink() } catch (e) { setError(apiError(e, 'Couldn’t create the payment link.')); setBusy(false); return }
    const sentVia: Channel[] = []; const sendErrors: string[] = []
    let contact = customer
    if (save && (needsPhone || needsEmail)) {
      try {
        const res = await apiClient.patch(`/api/contacts/${customer.id}`, { ...(needsPhone ? { phone } : {}), ...(needsEmail ? { email: addr.trim() } : {}) })
        contact = { ...customer, ...(res.data?.data ?? {}), ...(needsPhone ? { phone } : {}), ...(needsEmail ? { email: addr.trim() } : {}) }
        onContact(contact)
      } catch { sendErrors.push('Couldn’t save the new contact details, but the link was still sent.') }
    }
    if (text) {
      try {
        await apiClient.post('/api/inbox/send', {
          ...(contact.phone ? { contactId: customer.id } : { phoneNumber: phone }),
          channel: 'sms',
          body: message(link.url, link.feeCents ?? 0),
        }, { headers: { 'Idempotency-Key': crypto.randomUUID() } })
        sentVia.push('sms')
      } catch (e) { sendErrors.push(`Text: ${apiError(e, 'didn’t send.')}`) }
    }
    if (email) {
      try {
        await apiClient.post(`/api/invoices/${invoice.id}/send`, {
          to: addr.trim(),
          subject: `Invoice ${invoice.invoiceNumber} from ${business || 'us'}`,
          message: `Hi ${customer.firstName},\n\nYour invoice ${invoice.invoiceNumber} is attached. You can pay ${dollars(amountCents)} securely ${link.feeCents ? 'by card' : 'by card or bank'} here:\n${link.url}\n${feeNote(link.feeCents ?? 0).trim()}\n\nThank you,\n${business || ''}`.trim(),
        })
        sentVia.push('email')
      } catch (e) { sendErrors.push(`Email: ${apiError(e, 'didn’t send.')}`) }
    }
    setBusy(false)
    onDone({ kind: 'link', amountCents, url: link.url, sentVia, sendErrors })
  }

  return (
    <>
      {error && <p role="alert" className="pay-error">{error}</p>}
      <h3 className="pay-sec">Send to {customer.firstName}</h3>
      <ul className="ds-set-card pay-list">
        <li>
          <button type="button" role="checkbox" aria-checked={text} className="pay-row" onClick={() => setText(!text)}>
            <span className="ds-ico pay-ico" aria-hidden><MessageSquare /></span>
            <span className="pay-row-l"><b>Text the link</b><span>{customer.phone ? phoneOf(customer.phone) : 'No phone number on file'}</span></span>
            <span className={cn('pay-check', text && 'is-on')} aria-hidden />
          </button>
          {needsPhone && <input className="ds-input pay-inline" aria-label="Mobile number" inputMode="tel" placeholder="Mobile number" value={phone} onChange={e => setPhone(e.target.value)} />}
        </li>
        <li>
          <button type="button" role="checkbox" aria-checked={email} className="pay-row" onClick={() => setEmail(!email)}>
            <span className="ds-ico pay-ico" aria-hidden><Mail /></span>
            <span className="pay-row-l"><b>Email the invoice</b><span>{customer.email ? `PDF and pay link to ${customer.email}` : 'No email on file'}</span></span>
            <span className={cn('pay-check', email && 'is-on')} aria-hidden />
          </button>
          {needsEmail && <input className="ds-input pay-inline" aria-label="Email address" inputMode="email" placeholder="Email address" value={addr} onChange={e => setAddr(e.target.value)} />}
        </li>
      </ul>
      {(needsPhone || needsEmail) && (
        <label className="pay-save"><input type="checkbox" checked={save} onChange={e => setSave(e.target.checked)} /> Save to {customer.firstName}’s contact</label>
      )}
      {text && <p className="pay-preview">{message('[secure link]')}</p>}
      {!phoneOk && <p className="pay-note is-ask">{phone ? 'Enter a 10-digit US mobile number.' : 'Add a mobile number to text the link, or untick Text.'}</p>}
      {!emailOk && <p className="pay-note is-ask">{addr ? 'That email address doesn’t look right.' : 'Add an email address to send the invoice, or untick Email.'}</p>}
      <button type="button" className="btn-primary pay-cta" disabled={busy || (!text && !email) || !phoneOk || !emailOk} onClick={() => void send()}>
        {busy ? 'Sending…' : label}
      </button>
      <p className="pay-note pay-center">The link works once. Making a new one, or recording another payment, turns it off.</p>
    </>
  )
}
